import { devLog, devError } from '../utils/devLogger';
/**
 * Template Submission Service
 * Handles the "Perfect Copy" workflow for fillable templates
 * - Learners download blank templates
 * - Fill them in
 * - Re-upload completed versions
 * - Facilitators review submissions
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  limit,
  arrayUnion
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import NotificationService from './notificationService';

class TemplateSubmissionService {
  /**
   * Get all fillable templates
   * @returns {Promise<Array>} Array of fillable template resources
   */
  async getFillableTemplates(filters = {}) {
    try {
      const resourcesRef = collection(db, 'resources');
      let q = query(
        resourcesRef,
        where('isFillableTemplate', '==', true),
        where('status', '==', 'active'),
        orderBy('uploadedAt', 'desc')
      );

      let snapshot;
      try {
        snapshot = await getDocs(q);
      } catch (error) {
        // Fallback for missing index: pull all templates and filter client-side
        snapshot = await getDocs(query(resourcesRef, where('isFillableTemplate', '==', true)));
      }

      let templates = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          uploadedAt: data.uploadedAt?.toDate() || new Date(),
          templateDueDate: data.templateDueDate?.toDate() || null
        };
      });

      if (filters.programId) {
        templates = templates.filter(t => !t.programId || t.programId === filters.programId);
      }
      if (Array.isArray(filters.cohortIds) && filters.cohortIds.length) {
        templates = templates.filter((template) => {
          const cohortIds = Array.isArray(template.cohortIds)
            ? template.cohortIds
            : (template.cohortId ? [template.cohortId] : []);
          return cohortIds.some((id) => filters.cohortIds.includes(id));
        });
      }

      return templates;
    } catch (error) {
      devError('Error fetching fillable templates:', error);
      throw error;
    }
  }

  /**
   * Get learner's submissions for a specific template
   * @param {string} learnerId - Learner's user ID
   * @param {string} templateId - Template resource ID
   * @returns {Promise<Array>} Array of submissions
   */
  async getLearnerSubmissions(learnerId, templateId = null) {
    try {
      const submissionsRef = collection(db, 'templateSubmissions');
      let q;

      if (templateId) {
        q = query(
          submissionsRef,
          where('learnerId', '==', learnerId),
          where('templateId', '==', templateId),
          orderBy('submittedAt', 'desc')
        );
      } else {
        q = query(
          submissionsRef,
          where('learnerId', '==', learnerId),
          orderBy('submittedAt', 'desc')
        );
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        submittedAt: doc.data().submittedAt?.toDate() || new Date(),
        reviewedAt: doc.data().reviewedAt?.toDate() || null,
        dueDate: doc.data().dueDate?.toDate() || null
      }));
    } catch (error) {
      devError('Error fetching learner submissions:', error);
      throw error;
    }
  }

  /**
   * Get all submissions for a template (facilitator view)
   * @param {string} templateId - Template resource ID
   * @param {string} status - Optional status filter
   * @returns {Promise<Array>} Array of all submissions
   */
  async getTemplateSubmissions(templateId, status = null) {
    try {
      const submissionsRef = collection(db, 'templateSubmissions');
      let q;

      if (status) {
        q = query(
          submissionsRef,
          where('templateId', '==', templateId),
          where('status', '==', status),
          orderBy('submittedAt', 'desc')
        );
      } else {
        q = query(
          submissionsRef,
          where('templateId', '==', templateId),
          orderBy('submittedAt', 'desc')
        );
      }

      const snapshot = await getDocs(q);
      const submissions = [];

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();

        // Fetch learner details
        let learnerName = data.learnerName || 'Unknown';
        let learnerEmail = '';

        try {
          const userDoc = await getDoc(doc(db, 'users', data.learnerId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            learnerName = userData.displayName || userData.email;
            learnerEmail = userData.email;
          }
        } catch (error) {
          devLog('Could not fetch learner details:', error);
        }

        submissions.push({
          id: docSnap.id,
          ...data,
          learnerName,
          learnerEmail,
          submittedAt: data.submittedAt?.toDate() || new Date(),
          reviewedAt: data.reviewedAt?.toDate() || null,
          dueDate: data.dueDate?.toDate() || null
        });
      }

      return submissions;
    } catch (error) {
      devError('Error fetching template submissions:', error);
      throw error;
    }
  }

  /**
   * Submit a completed template
   * @param {Object} submissionData - Submission data
   * @param {File} file - The completed template file
   * @param {Function} progressCallback - Upload progress callback
   * @returns {Promise<string>} Submission ID
   */
  async submitTemplate(submissionData, file, progressCallback = null) {
    try {
      // Validate file
      if (!file) {
        throw new Error('No file provided');
      }

      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        throw new Error('File size exceeds 10MB limit');
      }

      // Upload file to storage
      const fileName = `${Date.now()}_${file.name}`;
      const storagePath = `template-submissions/${submissionData.templateId}/${submissionData.learnerId}/${fileName}`;
      const storageRef = ref(storage, storagePath);

      const uploadTask = uploadBytesResumable(storageRef, file);

      // Monitor upload progress
      if (progressCallback) {
        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            progressCallback(progress);
          }
        );
      }

      // Wait for upload to complete
      await uploadTask;

      // Get download URL
      const fileURL = await getDownloadURL(uploadTask.snapshot.ref);

      // Check for existing submission
      const submissionsRef = collection(db, 'templateSubmissions');
      const existingQuery = query(
        submissionsRef,
        where('learnerId', '==', submissionData.learnerId),
        where('templateId', '==', submissionData.templateId),
        orderBy('submittedAt', 'desc'),
        limit(1)
      );
      const existingSnapshot = await getDocs(existingQuery);
      const existingDoc = existingSnapshot.docs[0] || null;

      // Create/update submission document
      const submission = {
        templateId: submissionData.templateId,
        templateTitle: submissionData.templateTitle,
        learnerId: submissionData.learnerId,
        learnerName: submissionData.learnerName,
        learnerEmail: submissionData.learnerEmail || '',
        fileURL: fileURL,
        fileName: file.name,
        fileSize: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
        storagePath: storagePath,
        submittedAt: serverTimestamp(),
        status: 'pending', // pending, approved, rejected, revision_requested
        comments: submissionData.comments || '',
        dueDate: submissionData.dueDate || null,
        submittedById: submissionData.submittedById || submissionData.learnerId,
        submittedByName: submissionData.submittedByName || submissionData.learnerName,
        submittedByRole: submissionData.submittedByRole || 'learner',
        submittedOnBehalf: Boolean(submissionData.submittedOnBehalf)
      };

      const templateDoc = await getDoc(doc(db, 'resources', submissionData.templateId));
      const templateData = templateDoc.exists() ? templateDoc.data() : null;
      const notifyFacilitatorId = templateData?.uploadedById || templateData?.createdById || null;

      if (existingDoc) {
        const existingData = existingDoc.data();
        const previousVersions = Array.isArray(existingData.previousVersions) ? [...existingData.previousVersions] : [];
        if (existingData.fileURL) {
          previousVersions.push({
            fileURL: existingData.fileURL,
            fileName: existingData.fileName || '',
            submittedAt: existingData.submittedAt || null,
            status: existingData.status || 'pending',
            comment: existingData.reviewComments || '',
            replacedAt: new Date()
          });
        }

        await updateDoc(doc(db, 'templateSubmissions', existingDoc.id), {
          ...submission,
          status: 'pending',
          reviewComments: '',
          reviewerName: '',
          reviewedBy: '',
          reviewedAt: null,
          updatedAt: serverTimestamp(),
          previousVersions
        });

        const result = {
          id: existingDoc.id,
          ...submission,
          submittedAt: new Date()
        };
        if (notifyFacilitatorId) {
          await NotificationService.notify(
            'template_submission',
            'Template Resubmitted',
            `${submissionData.learnerName} resubmitted "${submissionData.templateTitle}".`,
            notifyFacilitatorId,
            { category: 'template_submissions', additionalData: { templateId: submissionData.templateId } }
          );
        }
        return result;
      }

      const docRef = await addDoc(collection(db, 'templateSubmissions'), submission);
      const result = {
        id: docRef.id,
        ...submission,
        submittedAt: new Date()
      };
      if (notifyFacilitatorId) {
        await NotificationService.notify(
          'template_submission',
          'New Template Submission',
          `${submissionData.learnerName} submitted "${submissionData.templateTitle}".`,
          notifyFacilitatorId,
          { category: 'template_submissions', additionalData: { templateId: submissionData.templateId } }
        );
      }
      return result;
    } catch (error) {
      devError('Error submitting template:', error);
      throw error;
    }
  }

  /**
   * Review a template submission (facilitator/admin)
   * @param {string} submissionId - Submission ID
   * @param {Object} reviewData - Review data
   * @returns {Promise<void>}
   */
  async reviewSubmission(submissionId, reviewData) {
    try {
      const submissionRef = doc(db, 'templateSubmissions', submissionId);

      const updateData = {
        status: reviewData.status, // approved, rejected, revision_requested
        reviewedBy: reviewData.reviewerId,
        reviewerName: reviewData.reviewerName,
        reviewedAt: serverTimestamp(),
        reviewComments: reviewData.comments || '',
        grade: reviewData.grade || null,
        reviewHistory: arrayUnion({
          status: reviewData.status,
          comment: reviewData.comments || '',
          reviewerId: reviewData.reviewerId,
          reviewerName: reviewData.reviewerName,
          reviewedAt: new Date()
        })
      };

      await updateDoc(submissionRef, updateData);

      const submissionSnap = await getDoc(submissionRef);
      if (submissionSnap.exists()) {
        const submission = submissionSnap.data();
        if (submission.learnerId) {
          await NotificationService.notify(
            'template_submission',
            'Template Status Updated',
            `Your template "${submission.templateTitle}" was marked as ${reviewData.status}.`,
            submission.learnerId,
            { category: 'template_submissions', additionalData: { submissionId } }
          );
        }
      }
    } catch (error) {
      devError('Error reviewing submission:', error);
      throw error;
    }
  }

  /**
   * Get submission statistics for a template
   * @param {string} templateId - Template resource ID
   * @returns {Promise<Object>} Statistics object
   */
  async getSubmissionStats(templateId) {
    try {
      const submissions = await this.getTemplateSubmissions(templateId);

      const stats = {
        total: submissions.length,
        pending: submissions.filter(s => s.status === 'pending').length,
        approved: submissions.filter(s => s.status === 'approved').length,
        rejected: submissions.filter(s => s.status === 'rejected').length,
        revisionRequested: submissions.filter(s => s.status === 'revision_requested').length
      };

      return stats;
    } catch (error) {
      devError('Error getting submission stats:', error);
      throw error;
    }
  }

  /**
   * Check if learner has submitted a template
   * @param {string} learnerId - Learner ID
   * @param {string} templateId - Template ID
   * @returns {Promise<boolean>} True if submitted
   */
  async hasSubmitted(learnerId, templateId) {
    try {
      const submissions = await this.getLearnerSubmissions(learnerId, templateId);
      return submissions.length > 0;
    } catch (error) {
      devError('Error checking submission status:', error);
      return false;
    }
  }

  /**
   * Get latest submission for a template
   * @param {string} learnerId - Learner ID
   * @param {string} templateId - Template ID
   * @returns {Promise<Object|null>} Latest submission or null
   */
  async getLatestSubmission(learnerId, templateId) {
    try {
      const submissions = await this.getLearnerSubmissions(learnerId, templateId);
      return submissions.length > 0 ? submissions[0] : null;
    } catch (error) {
      devError('Error getting latest submission:', error);
      return null;
    }
  }

  /**
   * Delete a submission (admin only)
   * @param {string} submissionId - Submission ID
   * @returns {Promise<void>}
   */
  async deleteSubmission(submissionId) {
    try {
      const submissionRef = doc(db, 'templateSubmissions', submissionId);
      await updateDoc(submissionRef, {
        status: 'deleted',
        deletedAt: serverTimestamp()
      });
    } catch (error) {
      devError('Error deleting submission:', error);
      throw error;
    }
  }
}

const templateSubmissionService = new TemplateSubmissionService();
export default templateSubmissionService;
